/**
 * *Fix and re-solve* — the pure half of the flow (#1960): the date presets,
 * the reach (#1987) and the pins sent to the repair.
 *
 * The range defaults to the days the re-solve may reach (#1987, founder
 * decision 3 Oct 2026: 5 by default, a team setting) from the first pinned
 * date, clipped to the last published date. *Week*, *Fortnight* and
 * *Custom* stay (*Week* is not shown when the days preset already runs 7
 * days, nor *Fortnight* at 14). The reach also caps the shifts moved — a
 * third of the team by default — and *Widen* doubles the shifts; the days
 * stay as the lead set them (founder, 3 Oct 2026). Pins past the range are
 * named, with one click to stretch the days over them (#1997). The
 * toolbar's standing door (#2029) opens on the dates on screen instead, with
 * the absences on seats still held pinned off (`absencesOnHeldSeats`).
 */

export type RangePreset = "days" | "week" | "fortnight" | "custom";

/** The defaults before the team's own are read (the server's defaults). */
export const DEFAULT_DAYS_FORWARD = 5;
export const DEFAULT_SHIFTS_MOVED = 3;

export const RANGE_PRESETS: { key: RangePreset; label: string }[] = [
  { key: "days", label: "Days" },
  { key: "week", label: "Week" },
  { key: "fortnight", label: "Fortnight" },
  { key: "custom", label: "Custom" },
];

/**
 * The presets to show: *Week* is hidden when the days preset already runs
 * 7 days, *Fortnight* when it runs 14 — one button per range.
 */
export function visiblePresets(days: number): { key: RangePreset; label: string }[] {
  return RANGE_PRESETS.filter(
    (p) => !(p.key === "week" && days === 7) && !(p.key === "fortnight" && days === 14),
  );
}

/** The preset in effect: a hidden *Week* / *Fortnight* reads as the days preset. */
export function effectivePreset(preset: RangePreset, days: number): RangePreset {
  return visiblePresets(days).some((p) => p.key === preset) ? preset : "days";
}

/** A preset's button label: the days preset names its count. */
export function presetLabel(preset: RangePreset, days: number): string {
  if (preset === "days") return `${days} day${days === 1 ? "" : "s"}`;
  return RANGE_PRESETS.find((p) => p.key === preset)?.label ?? preset;
}

function parse(iso: string): Date {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function fmt(dt: Date): string {
  return dt.toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  const dt = parse(iso);
  dt.setUTCDate(dt.getUTCDate() + days);
  return fmt(dt);
}

export interface DateRange {
  from: string;
  to: string;
}

/** The days each fixed preset reaches. */
const PRESET_DAYS = { week: 7, fortnight: 14 } as const;

/**
 * The range a preset names, from `date` (the first pinned date). `days` is
 * the days preset's count. `lastDay` (the last published date, else the
 * window's last day) caps the end; it never pulls the end before the start.
 * `custom` returns the custom pair as given.
 */
export function presetRange(
  preset: RangePreset,
  date: string,
  lastDay: string | null,
  custom?: DateRange,
  days: number = DEFAULT_DAYS_FORWARD,
): DateRange {
  if (preset === "custom" && custom) return custom;
  const reach =
    preset === "week" || preset === "fortnight"
      ? PRESET_DAYS[preset]
      : Math.max(1, Math.round(days));
  const to = addDays(date, reach - 1);
  const capped = lastDay && lastDay >= date && lastDay < to ? lastDay : to;
  return { from: date, to: capped };
}

/** Inclusive day count of a range (0 when it runs backwards). */
export function dayCount(range: DateRange): number {
  const n = Math.round((parse(range.to).getTime() - parse(range.from).getTime()) / 86_400_000) + 1;
  return n > 0 ? n : 0;
}

/** Every ISO date of a range, in order. */
export function datesOf(range: DateRange): string[] {
  const out: string[] = [];
  for (let i = 0; i < dayCount(range) && i < 62; i += 1) out.push(addDays(range.from, i));
  return out;
}

/**
 * The days a range reaches forward from `anchor` (the first pinned date) —
 * what the repair is told it may reach: the preset's own count (7 for a
 * week even when the last published date clips it), never less than the
 * range itself runs past the anchor.
 */
export function rangeDaysForward(
  preset: RangePreset,
  days: number,
  anchor: string,
  range: DateRange,
): number {
  const runs = dayCount({ from: anchor, to: range.to });
  const own =
    preset === "week" || preset === "fortnight"
      ? PRESET_DAYS[preset]
      : preset === "days"
        ? days
        : runs;
  return Math.max(1, own, runs);
}

/** How far a repair may reach: shifts moved beyond the pins, days forward. */
export interface Reach {
  shifts: number;
  days: number;
}

/**
 * *Widen* (#1987; founder, 3 Oct 2026): one click doubles the shifts. The
 * days stay as they are — the lead changes them by hand.
 */
export function widenReach(r: Reach): Reach {
  return { shifts: Math.max(1, r.shifts * 2), days: r.days };
}

/** The first pinned date on or after `date`, else `date` itself. */
export function firstPinDate(pins: readonly { date: string }[], date: string): string {
  let first: string | null = null;
  for (const p of pins) if (p.date >= date && (first === null || p.date < first)) first = p.date;
  return first ?? date;
}

export interface FlowPin {
  membership_id: string;
  display_name: string;
  date: string;
  off: boolean;
  station?: string | null;
  session?: string | null;
}

export function pinKey(p: Pick<FlowPin, "membership_id" | "date" | "off" | "station" | "session">) {
  return `${p.membership_id}|${p.date}|${p.off ? "off" : `${p.station ?? ""}|${p.session ?? ""}`}`;
}

/** The pins that fall on the range — the ones the repair is sent. */
export function pinsInRange<T extends { date: string }>(pins: readonly T[], range: DateRange): T[] {
  return pins.filter((p) => p.date >= range.from && p.date <= range.to);
}

/** The off pins every absent member starts with: one per absence date. */
export function absencePins(
  absent: readonly { membership_id: string; display_name: string; dates: readonly string[] }[],
): FlowPin[] {
  const out: FlowPin[] = [];
  for (const a of absent) {
    for (const date of a.dates) {
      out.push({ membership_id: a.membership_id, display_name: a.display_name, date, off: true });
    }
  }
  return out;
}

/** The most days a re-solve may reach (the server's `MAX_REPAIR_DAYS`). */
export const MAX_DAYS_FORWARD = 62;

/** Pins dated after the range: left out of the run (#1997). */
export interface LeftOut {
  /** Distinct pinned dates after the range. */
  dates: number;
  /** Every one of those pins is an absence (`off`). */
  allOff: boolean;
  /** The last of those dates, or null when nothing is left out. */
  last: string | null;
}

export function pinsAfterRange(
  pins: readonly { date: string; off: boolean }[],
  range: DateRange,
): LeftOut {
  const after = pins.filter((p) => p.date > range.to);
  const dates = new Set(after.map((p) => p.date));
  let last: string | null = null;
  for (const d of dates) if (last === null || d > last) last = d;
  return { dates: dates.size, allOff: after.every((p) => p.off), last };
}

/** The line under the pins when some fall after the range (#1997). */
export function leftOutText(left: LeftOut): string {
  const one = left.dates === 1;
  const what = left.allOff
    ? `${left.dates} day${one ? "" : "s"} of this absence`
    : `${left.dates} pinned date${one ? "" : "s"}`;
  return `${what} ${one ? "falls" : "fall"} after these dates and ${one ? "is" : "are"} left out.`;
}

/** The days preset count that reaches from `anchor` to `last` (at most 62). */
export function stretchDays(anchor: string, last: string): number {
  return Math.min(MAX_DAYS_FORWARD, Math.max(1, dayCount({ from: anchor, to: last })));
}

/** A roster row, as far as the toolbar door needs it: its holder, its date,
 *  and `absent` — the roster read's own flag for a seat whose holder has an
 *  active MC or leave row on that date (the struck *off sick* / *on leave*
 *  chip the grid and the List view draw). */
export interface HeldSeatRow {
  member_id: string;
  date: string;
  absent?: "mc" | "leave" | null;
}

/**
 * #2029 — the absences the toolbar's standing *Fix and re-solve* pins off:
 * a person on a date only when they still hold a seat on it in the roster
 * on screen AND are off that date (`absent` set on the row) — an MC or a
 * leave that landed after the solve, covered or not. Planned leave the
 * solve already honoured has no seat under it and is not pinned. One entry
 * per person with each such date in `range`, ordered by their first date
 * and then by name.
 */
export function absencesOnHeldSeats(
  rows: readonly HeldSeatRow[],
  names: ReadonlyMap<string, string>,
  range: DateRange,
): { membership_id: string; display_name: string; dates: string[] }[] {
  const byMember = new Map<string, Set<string>>();
  for (const row of rows) {
    if (!row.absent) continue;
    if (row.date < range.from || row.date > range.to) continue;
    const set = byMember.get(row.member_id) ?? new Set<string>();
    set.add(row.date);
    byMember.set(row.member_id, set);
  }
  return [...byMember.entries()]
    .map(([membership_id, set]) => ({
      membership_id,
      display_name: names.get(membership_id) ?? "Member",
      dates: [...set].sort(),
    }))
    .sort(
      (a, b) =>
        (a.dates[0] ?? "").localeCompare(b.dates[0] ?? "") ||
        a.display_name.localeCompare(b.display_name),
    );
}

/** #2029 — the dates on screen, cut to the most a re-solve may reach. */
export function repairDatesFor(shown: DateRange): DateRange {
  const last = addDays(shown.from, MAX_DAYS_FORWARD - 1);
  return { from: shown.from, to: shown.to < last ? shown.to : last };
}
